import React, {useState, useEffect, Suspense, useCallback, useMemo} from "react";
import {
    VsiBubble,
    VsiH4,
    VsiMedLabel,
    VsiP,
    VsiSpanText
} from "../../../styled/App.styled";
import {useSearchParams} from "react-router-dom";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {
    useAuthWithRoute,
    useDataWithRoute,
    useTitleFromComponent
} from "../../../utils/hooks";
import {
    Row,
    Col,
    Button,
    ButtonGroup,
    Input,
    Card,
    FormGroup,
    UncontrolledTooltip,
    InputGroup,
    Spinner
} from "reactstrap";
import Grid from "../../../components/Grid";
import Stockchart from "../../../components/Stockchart";
import {
    enterpriseCapacityPlanDefs,
    enterpriseCapacityPlanPhyDefs,
    getUserInputDefs
} from "../../../utils/gridColumnDefs/storage/enterpriseCapacityPlan";
import VsiModal from "../../../components/VsiModal";
import {
    formatDualAxisStockChartConfig
} from "../../../utils/Helpers";
import {DeleteAuth, PostAuth, PutAuth} from "../../../utils/Verbs";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import dayjs from "dayjs";
import {VsiSelectLabel} from "../../../styled/VsiLabeledSelect.styled";
import styles from "../../../utils/themeStyles";
import {forecastData, weightedGrowthRate} from "../../../utils/growthRateForecastUtils";

const NeedInput = React.lazy(() => import("../../../components/NeedInput"));
const NeedDate = React.lazy(() => import("../../../components/NeedDate"));

const mmYyRegex = /^(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

const allUnits = [
    {value: "application", label: "Application", route: "by_application"},
    {
        value: "classification",
        label: "Classification",
        route: "by_classification"
    },
    {value: "device_type_code", label: "Device Type", route: "by_device_type"},
    {value: "data_center", label: "Data Center", route: "by_data_center"}
];


const chartUtils = {
    capacity: {
        fieldsArr: [
            "used",
            "effective_capacity",
            "subscription_capacity",
            "order_capacity",
            "physical_used",
            "physical_effective_capacity",
            "physical_subscription_capacity",
            "physical_order_capacity"
        ],
        fieldsObjToGrowthRate: [
            "used",
            "effective_capacity",
            "subscription_capacity",
            "order_capacity",
            "physical_used",
            "physical_effective_capacity",
            "physical_subscription_capacity",
            "physical_order_capacity"
        ],
        chartConfig: {
            physical: [
                {name: "Physical Used", field: "physical_used", yAxis: 0},
                {name: "Physical Capacity", field: "physical_effective_capacity", yAxis: 0},
                {
                    name: "80% Capacity",
                    field: "physical_subscription_capacity",
                    yAxis: 0
                },
                {
                    name: "70% Capacity",
                    field: "physical_order_capacity",
                    yAxis: 0
                }
            ],
            effective: [
                {name: "Used", field: "used", yAxis: 0},
                {name: "Effective Capacity", field: "effective_capacity", yAxis: 0},
                {name: "80% Capacity", field: "subscription_capacity", yAxis: 0},
                {name: "70% Capacity", field: "order_capacity", yAxis: 0}
            ]
        },
        metricFields: {
            physical: {
                capacity: "physical_effective_capacity",
                workload: "physical_used"
            },
            effective: {
                capacity: "effective_capacity",
                workload: "used"
            }
        }
    },
    performance: {
        fieldsArr: [
            "used_iops",
            "capacity_iops",
            "subscription_capacity_iops",
            "order_capacity_iops",
        ],
        fieldsObjToGrowthRate: [
            'used_iops',
            'capacity_iops',
            'subscription_capacity_iops',
            'order_capacity_iops'
        ],
        chartConfig: [
            {name: "IOPS Used", field: "used_iops"},
            {name: "IOPS Capacity", field: "capacity_iops"},
            {
                name: "80% Capacity",
                field: "subscription_capacity_iops",
            },
            {name: "70% Capacity", field: "order_capacity_iops"},
        ],
        metricFields: {
            capacity: "capacity_iops",
            workload: "used_iops",
        },
    },
};

const inputDefault = {
    month: "Month",
    actualDate: "",
    workload: "",
    capacity: "",
    growth: "",
    growthType: "growth",
    notes: ""
};

export default function EnterpriseCapacityPlan({context, title}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const {theme, client, periodId, user, getAccessTokenSilently} = useVsiAppContext();

    const [gridData, setGridData] = useState([]);

    const searchUnit = searchParams.get("unit");
    const searchSecondaryUnit = searchParams.get("secondaryUnit");
    let defaultUnit = allUnits[0];
    let secondaryDefault = {value: "all", label: "All"};
    if (searchUnit) {
        defaultUnit = allUnits.find(({value}) => searchParams.get("unit") === value) || defaultUnit;
    }
    if (searchSecondaryUnit) {
        secondaryDefault = allUnits.find(({value}) => searchParams.get("secondaryUnit") === value) || secondaryDefault;
    }
    const [unit, setUnit] = useState(defaultUnit);
    const [secondaryUnit, setSecondaryUnit] = useState(secondaryDefault);

    const [loadNeedInput, setLoadNeedInput] = useState(0);
    const [loadNeedDate, setLoadNeedDate] = useState(0);
    const [capacityTrend, setCapacityTrend] = useState([]);
    const [capacityTrendConfig, setCapacityTrendConfig] = useState({});
    const [capacityTrendType, setCapacityTrendType] = useState("effective");
    const [forecastType, setForecastType] = useState("Months");
    const [chartType, setChartType] = useState("capacity");
    const [forecastLength, setForecastLength] = useState(12);
    const [adjustModalOpen, setAdjustModalOpen] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [noChangeModalOpen, setNoChangeModalOpen] = useState(false);
    const [userInputData, setUserInputData] = useState(inputDefault);
    const [allOtherUnits, setAllOtherUnits] = useState(allUnits);

    const [availableDates, setAvailableDates] = useState([]);
    const [flattenFuture, setFlattenFuture] = useState(false);
    const [planIsSaved, setPlanIsSaved] = useState(false);
    const [planApplied, setPlanIsApplied] = useState(false);
    const [activePlan, setActivePlan] = useState({});
    const [isSavingPlan, setIsSavingPlan] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState(availableDates?.[0] ?? {});
    const [additionalChartOption, setAdditionalChartOption] = useState({});
    const [secondaryAdditionalChartOption, setSecondaryAdditionalChartOption] = useState({});
    const [additionalChartOptionSelection, setAdditionalChartOptionSelection] = useState([]);
    const [secondaryChartOptionSelection, setSecondaryChartOptionSelection] = useState([]);
    const [modalData, setModalData] = useState([]);

    const userInputDefs = useMemo(() => getUserInputDefs(availableDates), [availableDates]);

    let authRoute = `/api/v6/admin/enterprise_capacity_plan/client/${client.clientId}/${unit.value}`
    if (secondaryAdditionalChartOption?.value) {
        authRoute += `/${encodeURI(secondaryAdditionalChartOption.value)}`;
    }
    const [savedChangesData, {isLoading: isPlanDataLoading, reFetchData: reFetchSavedPlanData}] = useAuthWithRoute(authRoute);

    useTitleFromComponent(`${context} / ${title}`);

    let dataRoute = `/api/v6/report/enterprise_capacity_plan_proc?unit=${unit.value}&client_id=${client.clientId}&period_id=${periodId}`;
    if (additionalChartOption?.value) {
        dataRoute += `&unit_name=${encodeURI(additionalChartOption.value)}`;
    }
    if (secondaryUnit?.value && secondaryUnit?.value !== 'all') {
        dataRoute += `&secondary_unit=${encodeURI(secondaryUnit.value)}`;
    }
    if (secondaryAdditionalChartOption?.value) {
        dataRoute += `&secondary_unit_name=${encodeURI(secondaryAdditionalChartOption.value)}`;
    }

    const [data, {isLoading: isReportLoading}] = useDataWithRoute(dataRoute);

    const isApplicationMissingInBu = data?.recordset?.[0]?.[unit.value] === "" && additionalChartOption?.value === "NOT ASSIGNED";

    const isLoading = isPlanDataLoading || 
        isReportLoading || 
        !additionalChartOption?.value || 
        (secondaryUnit?.value !== 'all' && !secondaryAdditionalChartOption?.value) ||
        (isApplicationMissingInBu ? false : (data?.recordset?.[0]?.[unit.value] ? additionalChartOption?.value !== data?.recordset?.[0]?.[unit.value] : false));

    useEffect(() => {
        setActivePlanFromSavedData();
    }, [savedChangesData, additionalChartOption]);

    useMemo(() => {
        let updatedOptions = allUnits.filter(value => value.value !== unit.value)
        setAllOtherUnits([{value: "all", label: "All"}, ...updatedOptions])
    }, [unit])

    // modal dates
    useEffect(() => {
        if (Object.keys(selectedMonth).length < 1 && availableDates.length) {
            setSelectedMonth(availableDates[0]);
            setUserInputData({
                ...userInputData,
                month: availableDates[0].value
            });
        }
    }, [availableDates]);

    useEffect(() => {
        const newData = Object.keys(activePlan)
            .filter((key) => key.match(mmYyRegex))
            .map((key) => activePlan[key]);

        setModalData(newData);
    }, [activePlan]);

    useEffect(() => {
        // Checking unique units so that it doesn't show the graph before the default unit data comes back
        let uniqueUnits = [];
        if (secondaryUnit) {
            let unitTypes = data?.recordset?.map(datum => datum[secondaryUnit.value]) || [];
            let uniques = new Set(unitTypes)
            uniqueUnits = [...uniques];
        }
        if (additionalChartOption?.value && 
            (isApplicationMissingInBu || 
                additionalChartOption?.value === data?.recordset?.[0]?.[unit.value] || 
                (secondaryAdditionalChartOption?.value &&
                    secondaryAdditionalChartOption?.value === data?.recordset?.[0]?.[secondaryUnit.value] &&
                    uniqueUnits.length < 2
                ) &&
                !isReportLoading
            )
        ) {
            handleStateWithData(data?.recordset || []);
        } else {
            handleStateWithData([]);
        }
    }, [
        data,
        searchParams,
        flattenFuture,
        activePlan,
        capacityTrendType,
        additionalChartOptionSelection,
        secondaryChartOptionSelection,
        additionalChartOption,
        secondaryAdditionalChartOption,
        forecastLength,
        chartType
    ]);

    useEffect(() => {
        if (data?.recordsets?.[1]) {
            const allOptions = data?.recordsets?.[1]?.map(datum => ({
                label: datum[unit.value],
                value: datum[unit.value],
                unit: unit.value
            }));
            setAdditionalChartOptionSelection(allOptions);
            if (!additionalChartOption) {
                let defaultOption = allOptions.find(({value}) => searchParams.get(`${unit.value}`) === value) || allOptions[0];
                setAdditionalChartOption(defaultOption);
            }
        }
    }, [data, unit.value]);

    useEffect(() => {
        if (data?.recordsets?.[2]) {
            const allOptions = data?.recordsets?.[2]?.map(datum => ({
                label: datum[secondaryUnit.value],
                value: datum[secondaryUnit.value],
                unit: secondaryUnit.value
            }));
            setSecondaryChartOptionSelection(allOptions);
            let defaultOption = allOptions.find(({value}) => searchParams.get(`${secondaryUnit.value}`) === value) || allOptions[0];
            setSecondaryAdditionalChartOption(defaultOption);
        }
    }, [data, secondaryUnit.value]);

    useEffect(() => {
        if (!additionalChartOption?.value || unit.value !== additionalChartOption.unit) {
            setAdditionalChartOption(additionalChartOptionSelection[0]);
        }
    }, [additionalChartOptionSelection]);

    useEffect(() => {
        if (!secondaryAdditionalChartOption?.value || secondaryUnit.value !== secondaryAdditionalChartOption.unit) {
            setSecondaryAdditionalChartOption(secondaryChartOptionSelection[0]);
        }
    }, [secondaryChartOptionSelection]);

    useEffect(() => {
        setPlanIsSaved(false);
    }, [userInputData, savedChangesData]);

    const onUnitChange = (selectedUnit) => {
        setUnit(selectedUnit);
        setSecondaryUnit({value: "all", label: "All"});
        setSecondaryAdditionalChartOption({});
        searchParams.delete("secondaryUnit")
        setSearchParams(
            {unit: selectedUnit.value},
            {replace: true}
        );
    };

    const onSecondaryUnitChange = (selectedUnit) => {
        setSecondaryUnit(selectedUnit);
        setSearchParams(
            {secondaryUnit: selectedUnit.value},
            {replace: true}
        );
    };

    const onMonthChange = (e) => {
        setSelectedMonth(e);
        setUserInputData(
            {
                ...inputDefault,
                ...activePlan[e.value],
                growthType: (activePlan?.[e.value]?.growthType) || "growth",
                month: e.value
            });
    };

    const handleCapacityPlan = (data) => {
        const capacity = {};
        const {fieldsArr, metricFields} = chartUtils?.[chartType];
        let flattenedCapacityValue = 0;
        let growthFlag = false;
        let growthNum = 0;
        let growthStartingPoint = 0;
        let growthIndex = 1;

        fieldsArr.forEach((field) => {
            capacity[field] = 0;
        });

        return (data || []).map((datum, index) => {
            const newDatum = {...datum};
            const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");

            const capacityField =
                metricFields?.[capacityTrendType]?.capacity || metricFields?.capacity;
            const workloadField =
                metricFields?.[capacityTrendType]?.workload || metricFields?.workload;
            if (
              activePlan?.[dateToCompare]?.chartType === chartType ||
                (!activePlan?.[dateToCompare]?.chartType && chartType === "capacity")
                || growthFlag
            ) {

                // growth types (before adding workload)
                if ((activePlan?.[dateToCompare]?.capacityType === capacityTrendType &&
                        activePlan?.[dateToCompare]?.growth) || growthFlag) {
                    growthFlag = true;
                    if (!growthNum && activePlan?.[dateToCompare]?.growth) {
                        if (!growthStartingPoint) {
                            growthStartingPoint = data[index - 1][workloadField];
                        }
                        if (activePlan?.[dateToCompare]?.growthType === "gibMonth") {
                            growthNum = parseFloat(activePlan?.[dateToCompare]?.growth);
                        } else if (activePlan?.[dateToCompare]?.growthType === "growth") {
                            growthNum = parseFloat(
                                growthStartingPoint *
                                (parseFloat(activePlan?.[dateToCompare]?.growth) / 100)
                            );
                        }
                    }
                    newDatum[workloadField] = growthStartingPoint + (growthNum * growthIndex);
                    growthIndex++;
                }

                // capacity
                if (activePlan?.[dateToCompare]?.capacityType === capacityTrendType &&
                  activePlan?.[dateToCompare]?.capacity) {
                    capacity[capacityField] =
                        capacity[capacityField] +
                        parseFloat(activePlan?.[dateToCompare]?.capacity);
                }

                // workload
                if (activePlan?.[dateToCompare]?.capacityType === capacityTrendType &&
                    activePlan?.[dateToCompare]?.workload) {
                    capacity[workloadField] =
                        capacity[workloadField] +
                        parseFloat(activePlan?.[dateToCompare]?.workload);
                }

                // flatten future capacity
            }

            if (flattenFuture) {
                if (!flattenedCapacityValue && datum.isForecasted) {
                    flattenedCapacityValue = data[index - 1][capacityField];
                }
                if (flattenedCapacityValue) {
                    newDatum[capacityField] = flattenedCapacityValue;
                }
            }

            fieldsArr.forEach((field) => {
                if (field.toLowerCase().includes("subscription")) {
                    newDatum[field] = parseFloat(newDatum[capacityField] * 0.8);
                } else if (field.toLowerCase().includes("order")) {
                    newDatum[field] = parseFloat(newDatum[capacityField] * 0.7);
                } else {
                    newDatum[field] = newDatum[field] + capacity[field];
                    // newDatum[field] = newDatum[field] + workload[field];
                }
            });

            return newDatum;
        });
    };

    const handleStateWithData = (data) => {
        const truncatedData = data.sort(function (a, b) {
            return new Date(a.collection_date) - new Date(b.collection_date);
        });

        const {fieldsArr, chartConfig, fieldsObjToGrowthRate} = chartUtils[chartType];

        const grObj = {};
        fieldsObjToGrowthRate.forEach(rate => {
            grObj[rate] = weightedGrowthRate(truncatedData.map(m => ({
                date: m.collection_date,
                value: m[rate]
            }))) * 30 //monthly;
        });
        const forecastedData = forecastData(
            truncatedData,
            fieldsArr,
            grObj,
            forecastLength,
            forecastType.toLowerCase() === "months" ? "month" : "year"
        );
        const allData = [...truncatedData, ...forecastedData];

        const planData = handleCapacityPlan(allData)
            .map(datum => {
                if (flattenFuture) {
                    return datum;
                }
                let effUsed = datum?.used ?? 0;
                let effCapacity = datum?.effective_capacity ?? 0;

                let physCapacity = datum?.physical_effective_capacity ?? 0;
                let physUsed = datum?.physical_used ?? 0;
                if (physUsed <= 0) {
                    physUsed = 0;
                }
                return {
                    ...datum,
                    used: effUsed > 0 ? effUsed : 0,
                    physical_used: physUsed > 0 ? (
                        physUsed > physCapacity ? physCapacity : physUsed
                    ) : 0,
                    effective_capacity: effCapacity < physCapacity ? physCapacity : effCapacity,
                    subscription_capacity: (effCapacity < physCapacity ? physCapacity : effCapacity) * .8,
                    order_capacity: (effCapacity < physCapacity ? physCapacity : effCapacity) * .7
                };
            }); // use capacity plan HERE

        const formattedData = formatDualAxisStockChartConfig(
            planData,
            chartConfig?.[capacityTrendType] || chartConfig
        );

        setCapacityTrend(
            formattedData.series.map((series) => ({
                ...series,
                zoneAxis: "x",
                zones: [
                    {
                        value: Date.parse(
                            truncatedData?.[truncatedData.length - 1]?.collection_date?.split?.("T")?.[0]
                        )
                    },
                    {
                        dashStyle: "Dash"
                    }
                ]
            }))
        );

        setCapacityTrendConfig({
            yAxis: [
                {
                    title: {
                        text: chartType === 'capacity' ? "GiB" : "IOPS",
                        position: "left"
                    }
                },
              chartType === 'capacity' ? {
                    title: {
                        text: "Dollars",
                    },
                    opposite: true,
                    labels: {format: '${value:,2f}'}
                } : {},
            ],
            plotOptions: {
                series: {
                    animation: false
                }
            }
        });

        setGridData(planData);

        setAvailableDates(
            forecastedData.map(({collection_date}) => {
                const month = dayjs(collection_date).format("MM/YYYY");
                return {
                    label: month,
                    value: month
                };
            })
        );
    };

    const setActivePlanFromSavedData = () => {
        if (savedChangesData.length > 0) {
            const savedData = savedChangesData?.[0]?.adjustments?.[additionalChartOption?.value] || [];

            const planData = {...savedChangesData?.[0]};
            (savedData || []).forEach((datum) => {
                if (!planData.hasOwnProperty(datum.month)) {
                    planData[datum.month] = {
                        ...datum,
                        growthType: datum?.growthType ?? "growth"
                    };
                }
            });
            delete planData.adjustments;

            // flatten future
            if (flattenFuture !== planData.flattenToggle) {
                setFlattenFuture(Boolean(planData.flattenToggle));
            }

            if (planData.forecastIntervals && planData.forecastType) {
                setForecastLength(planData.forecastIntervals);
                setForecastType(planData.forecastType);
            }
            setActivePlan(planData);
            setPlanIsApplied(true);
            setUserInputData(
                {
                    ...inputDefault,
                    ...planData[selectedMonth.value],
                    growthType: (planData?.[selectedMonth.value]?.growthType) || "growth",
                    month: selectedMonth.value
                });


        } else {
            setActivePlan({});
            setFlattenFuture(false);
        }
    };

    const onCapBtnClick = (e) => {
        const {name} = e.target;
        setCapacityTrendType(name);
    };

    const onGrowthBtnClick = (e) => {
        const {name} = e.target;
        setUserInputData(prevState => ({
            ...prevState,
            growthType: name
        }));
    };

    const onForecastTypeClick = (e) => {
        const {name} = e.target;
        if (name !== forecastType) {
            if (name === "Years") {
                let yearCheck =
                    parseInt(forecastLength / 12) === 0
                        ? 1
                        : parseInt(forecastLength / 12);
                setForecastLength(yearCheck);
            } else {
                setForecastLength(parseInt(forecastLength * 12));
            }

            setForecastType(name);
            handleStateWithData(data?.recordset, name);
        }
    };

    const onInputChange = (e) => {
        const {name, value} = e.target;
        setLoadNeedInput(0);
        setUserInputData({...userInputData, [name]: value});
    };

    const onIntervalChange = (e) => {
        const {value} = e.target;
        setLoadNeedInput(0);
        setForecastLength(value ? parseInt(value) : value);
    };

    const addInputs = () => {
        if (userInputData.month === "Month") {
            setLoadNeedDate(1);
        } else if (
            !userInputData.capacity &&
            !userInputData.workload &&
            !userInputData.growth
        ) {
            setLoadNeedInput(1);
        } else {
            // hw changes
            const planData = {...activePlan};

            if (!planData[userInputData.month]) {
                planData[userInputData.month] = {};
            }

            planData[userInputData.month].capacity = userInputData.capacity;
            planData[userInputData.month].workload = userInputData.workload;
            planData[userInputData.month].growth = userInputData.growth;
            planData[userInputData.month].notes = userInputData.notes;
            planData[userInputData.month].month = userInputData.month;
            planData[userInputData.month].capacityType = capacityTrendType;
            planData[userInputData.month].chartType = chartType;
            planData[userInputData.month].growthType = userInputData.growthType;
            setActivePlan(planData);
        }
    };

    const saveInputs = () => {
        setAdjustModalOpen(!adjustModalOpen);
        handleStateWithData(data?.recordset);
    };

    const formatMtd = (month) => {
        let splitMonth = month.split("/");
        let date = new Date(new Date().setMonth(splitMonth[0] - 1)).setFullYear(
            splitMonth[1]
        );
        return new Date(date).toLocaleDateString("en-us", {
            month: "numeric",
            day: "numeric",
            year: "numeric"
        });
    };

    const formatDtm = (date) => {
        let splitDate = date.split("/");
        let month = splitDate[0].length < 2 ? '0' + splitDate[0] : splitDate[0];
        let monthYear = month + "/" + splitDate[2]
        return monthYear;
    };

    const savePlan = async () => {
        // matching only keys that are months

        setIsSavingPlan(true);
        let monthKeys = Object.keys(activePlan).filter((key) =>
            key.match(mmYyRegex)
        );
        const adjustments = monthKeys.map((key) => {
            return {
                ...activePlan[key],
                month: key,
                actualDate: formatMtd(key)
            };
        });
        let savedPlan = {
            clientId: client.clientId,
            changedByUserEmail: user.email,
            unitType: unit.value,
            unitName: additionalChartOption.value,
            flattenToggle: flattenFuture,
            forecastType,
            forecastIntervals: forecastLength,
            adjustments: {
                ...savedChangesData?.[0]?.adjustments,
                [additionalChartOption.value]: adjustments
            }
        };
        
        if (secondaryAdditionalChartOption?.value) {
            savedPlan.secondUnit = secondaryUnit.value
            savedPlan.secondUnitName = secondaryAdditionalChartOption.value
        }
        if (!savedPlan.clientId) {
            console.error("No clientId.");
        } else {
            const token = await getAccessTokenSilently();
            if (savedChangesData.length > 0) {
                try {
                    const putData = await PutAuth(
                        `/api/v6/admin/enterprise_capacity_plan/${savedChangesData[0]._id}`,
                        savedPlan,
                        token
                    );
                    if (putData.status === 200) {
                        setPlanIsSaved(true);
                        reFetchSavedPlanData();
                    }
                } catch (err) {
                    console.error(err);
                }
            } else {
                if (Object.keys(savedPlan.adjustments).length === 0) {
                    setNoChangeModalOpen(!noChangeModalOpen);
                } else {
                    try {
                        const postData = await PostAuth(
                            `/api/v6/admin/enterprise_capacity_plan`,
                            savedPlan,
                            token
                        );
                        if (postData.status === 201) {
                            setPlanIsSaved(true);
                            reFetchSavedPlanData();
                            setTimeout(() => {
                                setPlanIsSaved(false);
                                setPlanIsApplied(true);
                            }, 3500);
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        }

        setIsSavingPlan(false);
    };

    const resetChanges = async (type) => {
        if (type === "local") {
            setActivePlanFromSavedData();
        }
        if (type === "saved") {
            var tempAdjObj = {...savedChangesData[0].adjustments};
            if (tempAdjObj) {
                delete tempAdjObj[additionalChartOption.value];
            }
            const savedPlan = {
                ...savedChangesData[0],
                adjustments: tempAdjObj || []
            };
            const token = await getAccessTokenSilently();
            if (Object.keys(savedPlan.adjustments).length > 0) {
                try {
                    await PutAuth(
                        `/api/v6/admin/enterprise_capacity_plan/${savedChangesData[0]._id}`,
                        savedPlan,
                        token
                    );
                    setPlanIsApplied(false);
                    reFetchSavedPlanData();
                } catch (err) {
                    console.error(err);
                }
            } else {
                try {
                    await DeleteAuth(
                        `/api/v6/admin/enterprise_capacity_plan/${savedChangesData[0]._id}`,
                        token
                    );
                    setPlanIsApplied(false);
                    reFetchSavedPlanData();
                } catch (err) {
                    console.error(err);
                }
            }
        }

        setResetModalOpen(!resetModalOpen);
    };

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter((node) => {
            rowData.push(node.data);
        });

        handleStateWithData(rowData || []);
    };

    const deleteRow = (e) => {
        if (e.event.target.tagName === "BUTTON" || e.event.target.tagName === "I") {
            const newPlan = {...activePlan};
            delete newPlan[e.node.data.month];
            setActivePlan(newPlan);
        }
    };

    const onAdditionalOptionChange = async (option) => {
        setAdditionalChartOption(option);
        setSecondaryUnit({value: "all", label: "All"});
        setSecondaryAdditionalChartOption({});
        setPlanIsApplied(false);
        searchParams.set(unit.value, encodeURI(option.value));
        setSearchParams(searchParams, {replace: true});
    };

    const onSecondaryAdditionalOptionChange = async (option) => {
        setSecondaryAdditionalChartOption(option);
        setPlanIsApplied(false);
        searchParams.set(secondaryUnit.value, encodeURI(option.value));
        setSearchParams(searchParams, {replace: true});
    };
    
    const onRowEditingStopped = useCallback(async (event) => {
        const updatedData = {...event.data};

        Object.keys(event.data).forEach(key => {
            if (updatedData[key] === "undefined" || !updatedData[key]) {
                delete updatedData[key];
            }
        });

        const planData = {...activePlan};
        
        if (planData[updatedData.month]) {
            planData[updatedData.month].capacity = updatedData.capacity ?? "";
            planData[updatedData.month].workload = updatedData.workload ?? "";
            planData[updatedData.month].growth = updatedData.growth ?? "";
            planData[updatedData.month].notes = updatedData.notes ?? "";
        } else {
            // Remove old month entry
            delete planData[formatDtm(updatedData.actualDate)]
            // Move to new month
            planData[updatedData.month] = {
                actualDate: formatMtd(updatedData.month),
                capacity: updatedData.capacity ?? "",
                capacityType: updatedData.capacityType,
                chartType: updatedData.chartType,
                growth: updatedData.growth ?? "",
                growthType: updatedData.growthType,
                month: updatedData.month,
                notes: updatedData.notes ?? "",
                workload: updatedData.workload ?? ""
            }
        }
        
        setActivePlan(planData);
    }, [context, modalData]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Enterprise Capacity Plan by {unit.label}</VsiH4>
            <VsiBubble noAutobot>
                <div
                    className={"d-flex flex-row align-items-start justify-content-between"}
                >
                    <div className={"d-flex flex-row align-items-start justify-content-start"}>
                        <VsiLabeledSelect
                          options={allUnits}
                          isLoading={isLoading}
                          label={"Select Unit"}
                          onChange={onUnitChange}
                          defaultValue={unit}
                        />
                        <VsiLabeledSelect
                          options={additionalChartOptionSelection}
                          isLoading={isLoading}
                          isDisabled={isLoading}
                          label={unit.label}
                          minWidth={200}
                          maxWidth={600}
                          onChange={onAdditionalOptionChange}
                          defaultValue={additionalChartOption}
                        />
                        <div className={"me-2"}>
                            <VsiSelectLabel>Planning Metric</VsiSelectLabel>
                            <div className="d-flex align-items-start justify-content-between">
                                <ButtonGroup style={{ zIndex: 0 }}>
                                    <Button
                                      name="effective"
                                      onClick={() => setChartType("capacity")}
                                      active={chartType === "capacity"}
                                      disabled={chartType === "capacity"}
                                    >
                                        Capacity (GiB)
                                    </Button>
                                    <Button
                                      name="physical"
                                      onClick={() => setChartType("performance")}
                                      active={chartType === "performance"}
                                      disabled={chartType === "performance"}
                                    >
                                        Performance (IOPS)
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </div>
                        {chartType === "capacity" && (
                          <div className={"me-2"}>
                            <VsiSelectLabel>Capacity Type</VsiSelectLabel>
                            <div className="d-flex align-items-start justify-content-between">
                                <ButtonGroup style={{ zIndex: 0 }}>
                                    <Button
                                      name="effective"
                                      onClick={onCapBtnClick}
                                      active={capacityTrendType === "effective"}
                                      disabled={capacityTrendType === "effective"}
                                    >
                                        Effective
                                    </Button>
                                    <Button
                                      name="physical"
                                      onClick={onCapBtnClick}
                                      active={capacityTrendType === "physical"}
                                      disabled={capacityTrendType === "physical"}
                                    >
                                        Physical
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </div>
                        )}
                        <div className={"me-2"}>
                            <VsiSelectLabel>Forecast by</VsiSelectLabel>
                            <div className="d-flex align-items-start justify-content-between">
                                <ButtonGroup style={{ zIndex: 0 }}>
                                    <Button
                                      name="Months"
                                      onClick={onForecastTypeClick}
                                      active={forecastType === "Months"}
                                      disabled={forecastType === "Months"}
                                    >
                                        Month
                                    </Button>
                                    <Button
                                      name="Years"
                                      onClick={onForecastTypeClick}
                                      active={forecastType === "Years"}
                                      disabled={forecastType === "Years"}
                                    >
                                        Year
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </div>
                        <div>
                            <VsiSelectLabel htmlFor="forecastLength">
                                {forecastType}
                            </VsiSelectLabel>
                            <Input
                              id="forecastLength"
                              name="forecastLength"
                              placeholder="0"
                              type="number"
                              style={{ maxWidth: 150 }}
                              value={forecastLength}
                              onChange={onIntervalChange}
                            />
                        </div>
                    </div>
                </div>
                <div className={"d-flex flex-row align-items-start justify-content-between"}>
                    <div className={"d-flex flex-row align-items-start justify-content-start"}>
                        <VsiLabeledSelect
                            options={allOtherUnits}
                            isLoading={isLoading}
                            label={"Secondary Unit"}
                            onChange={onSecondaryUnitChange}
                            defaultValue={secondaryUnit}
                        />
                        {secondaryUnit.value !== 'all' && <>
                            <VsiLabeledSelect
                                options={secondaryChartOptionSelection}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                                label={secondaryUnit.label}
                                minWidth={200}
                                maxWidth={600}
                                onChange={onSecondaryAdditionalOptionChange}
                                defaultValue={secondaryAdditionalChartOption}
                            />
                        </>}
                    </div>
                </div>
                <Stockchart
                  series={capacityTrend}
                  config={capacityTrendConfig}
                  isLoading={isLoading}
                  animation={false}
                  formatData="{point.y:,.0f}"
                  formatPoint="{series.name}: <b>{point.y:,.0f}</b>"
                />
                <Row>
                    <Col className={"d-flex flex-row align-items-start"}>
                        <div>
                            <VsiSelectLabel className="ms-2">Date Adjustments</VsiSelectLabel>
                            <div className="d-flex align-items-start justify-content-between ms-2">
                                <ButtonGroup className="me-2">
                                    <Button
                                      name="addValues"
                                        id={"edit-btn-plan"}
                                        onClick={() => {
                                            setAdjustModalOpen(!adjustModalOpen);
                                        }}
                                    >
                                        <i className={"fa-solid fa-pen-to-square"}/>
                                    </Button>
                                </ButtonGroup>
                                <UncontrolledTooltip
                                    placement={"bottom"}
                                    target={"edit-btn-plan"}
                                >
                                    Add/edit future capacity/workload
                                </UncontrolledTooltip>
                                <ButtonGroup>
                                    <Button
                                        name="save"
                                        id={"save-btn-plan"}
                                        color="success"
                                        onClick={savePlan}
                                    >
                                        {isSavingPlan ? (
                                            <Spinner size={"sm"} color={"secondary"}/>
                                        ) : planIsSaved ? (
                                            <i className={"fa-solid fa-check"}/>
                                        ) : (
                                            <i className={"fa-solid fa-floppy-disk"}/>
                                        )}
                                    </Button>
                                    <Button
                                        name="reset"
                                        id={"reset-btn-plan"}
                                        color="danger"
                                        onClick={() => setResetModalOpen(!resetModalOpen)}
                                    >
                                        <i className={"fa-solid fa-trash"}/>
                                    </Button>
                                </ButtonGroup>
                            </div>
                            <UncontrolledTooltip placement={"bottom"} target={"save-btn-plan"}>
                                Save adjustments
                            </UncontrolledTooltip>
                            <UncontrolledTooltip placement={"bottom"} target={"reset-btn-plan"}>
                                Reset adjustments
                            </UncontrolledTooltip>
                        </div>
                        <div className="ms-2 d-flex flex-column align-items-center justify-content-center me-2">
                            <VsiSelectLabel htmlFor="flattenFuture">
                                Flatten Future Capacity
                            </VsiSelectLabel>
                            <FormGroup switch>
                                <Input
                                    type="switch"
                                    role="switch"
                                    id="flattenFuture"
                                    checked={flattenFuture}
                                    onChange={() => setFlattenFuture(!flattenFuture)}
                                />
                            </FormGroup>
                        </div>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-end">
                        {planApplied ? (
                            <VsiP
                                style={{color: styles[theme].textColor}}
                                className="mx-2 mb-1 fst-italic text-end"
                            > Saved Plan is Applied.
                            </VsiP>
                        ) : null}
                        {planIsSaved ? (
                            <VsiP
                                style={{color: styles[theme].textColor}}
                                className="mx-2 mb-1 text-end"
                            >
                                Saved!
                            </VsiP>
                        ) : null}
                    </Col>
                </Row>
            </VsiBubble>
            <VsiBubble unit={unit?.value}
                       autobotInfo={{secondaryMetric: [chartType, additionalChartOption?.value, capacityTrendType]}}>
                <Grid
                    addStyle
                    data={gridData}
                    columnDefs={capacityTrendType === "effective" ? enterpriseCapacityPlanDefs : enterpriseCapacityPlanPhyDefs}
                    filterChanged={filterChanged}
                />
            </VsiBubble>
            <VsiModal
                size={"xl"}
                isOpen={adjustModalOpen}
                toggle={() => setAdjustModalOpen(!adjustModalOpen)}
                header={<VsiSpanText>Adjust Forecast Values</VsiSpanText>}
                footer={
                    <div>
                        <Button onClick={saveInputs} className="m-1">
                            Done
                        </Button>
                        <Button
                            onClick={() => {
                                setAdjustModalOpen(!adjustModalOpen);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            >
                <div>
                    <Row>
                        <Col>
                            {loadNeedInput ? (
                                <Suspense fallback={<div></div>}>
                                    <NeedInput/>
                                </Suspense>
                            ) : null}
                            {loadNeedDate ? (
                                <Suspense fallback={<div></div>}>
                                    <NeedDate/>
                                </Suspense>
                            ) : null}
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={4}>
                            <Row>
                                <Col
                                    style={{width: "100%", border: "none"}}
                                    className={`input-selector-${theme}`}
                                >
                                    <VsiLabeledSelect
                                        options={availableDates}
                                        label={"Date"}
                                        onChange={onMonthChange}
                                        defaultValue={selectedMonth}
                                    />
                                </Col>
                            </Row>
                            <div>
                                <VsiMedLabel htmlFor="capacity">
                                    Capacity ({chartType === "capacity" ? "GiB" : "IOPS"})
                                </VsiMedLabel>
                                <Input
                                    id="capacity"
                                    name="capacity"
                                    placeholder="0"
                                    type="number"
                                    value={userInputData.capacity}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <VsiMedLabel htmlFor="workload">
                                    Workload ({chartType === "capacity" ? "GiB" : "IOPS"})
                                </VsiMedLabel>
                                <Input
                                    id="workload"
                                    name="workload"
                                    placeholder="0"
                                    type="number"
                                    value={userInputData.workload}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <VsiMedLabel htmlFor="growth">Growth</VsiMedLabel>
                                <InputGroup>
                                    <Input
                                        id="growth"
                                        name="growth"
                                        placeholder="None"
                                        type="number"
                                        value={userInputData.growth}
                                        onChange={onInputChange}
                                    />
                                    <Button
                                        name="growth"
                                        onClick={onGrowthBtnClick}
                                        style={{fontSize: "12px"}}
                                        active={userInputData.growthType === "growth"}
                                        disabled={userInputData.growthType === "growth"}
                                    >
                                        Growth %
                                    </Button>
                                    <Button
                                        name="gibMonth"
                                        onClick={onGrowthBtnClick}
                                        style={{fontSize: "12px"}}
                                        active={userInputData.growthType === "gibMonth"}
                                        disabled={userInputData.growthType === "gibMonth"}
                                    >
                                        GiB/Month
                                    </Button>
                                </InputGroup>
                            </div>
                            <div>
                                <VsiMedLabel htmlFor="notes">
                                    Notes for Planning (200 character limit)
                                </VsiMedLabel>
                                <Input
                                    type="textarea"
                                    id="notes"
                                    name="notes"
                                    className={"w-100 mb-2"}
                                    maxLength={200}
                                    onChange={onInputChange}
                                    value={userInputData.notes}
                                />
                            </div>
                            <div>
                                <Button color="primary" onClick={addInputs}>
                                    Adjust
                                    <i className={"ms-1 fa-solid fa-plus-minus"}/>
                                </Button>
                            </div>
                        </Col>
                        <Col xs={12} md={8}>
                            <Grid
                                hideRecordCount
                                hideGridToolbar
                                data={modalData}
                                disableCustomColumns={true}
                                columnDefs={userInputDefs}
                                rowSelection="single"
                                onRowEditingStopped={onRowEditingStopped}
                                rowClicked={deleteRow}
                                stopEditingOnClick={true}
                            />
                        </Col>
                    </Row>
                </div>
            </VsiModal>
            <VsiModal
                size={"m"}
                isOpen={resetModalOpen}
                toggle={() => setResetModalOpen(!resetModalOpen)}
                header={<VsiSpanText>Reset local or saved changes?</VsiSpanText>}
                footer={
                    <div>
                        <Button onClick={() => resetChanges("local")} className="m-1">
                            Local
                        </Button>
                        {planApplied ? (
                            <Button
                                onClick={() => {
                                    resetChanges("saved");
                                }}
                            >
                                Saved
                            </Button>
                        ) : (
                            <Button disabled={true}>None Saved</Button>
                        )}
                    </div>
                }
            ></VsiModal>
            <VsiModal
                size={"m"}
                isOpen={noChangeModalOpen}
                toggle={() => setNoChangeModalOpen(!noChangeModalOpen)}
                header={<VsiSpanText>No Changes to Save.</VsiSpanText>}
                footer={
                    <div>
                        <Button onClick={() => setNoChangeModalOpen(!noChangeModalOpen)}>
                            Close
                        </Button>
                    </div>
                }
            ></VsiModal>
        </Card>
    );
}
